import { memo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import get from 'lodash/get';
import dompurify from 'dompurify';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';
import { USER_ROLES } from 'src/_shared/constants/userRolesEnum.js';
import {
	GetCompanyAnnouncementsSettings,
	queryAnnouncements,
} from 'src/new-announcements/graphqlQueries.js';

const MODERATOR_ROLES = [
	USER_ROLES.ADMIN,
	USER_ROLES.MANAGER,
	USER_ROLES.SUPER_ADMIN,
	USER_ROLES.SUPPORT_ADMIN,
];

const sanitizer = dompurify.sanitize;

const AnnouncementsCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		announcements = [],
		currentUser,
	}) => {
		const client = useApolloClient();
		const [announcementCount, setAnnouncementCount] = useState(null);

		const companyId = currentUser?.companyId;
		const userId = currentUser?.id;
		const displayAs = currentUser?.displayAs;
		const isModerator = MODERATOR_ROLES.includes(displayAs);

		useEffect(() => {
			if (!companyId || !userId) return;

			let cancelled = false;

			(async () => {
				try {
					const settingsResult = await client.query({
						query: GetCompanyAnnouncementsSettings,
						variables: { id: companyId },
						fetchPolicy: 'cache-first',
					});
					const showCount = get(
						settingsResult,
						'data.getCompany.announcementsSettings.showCount',
						false
					);

					if (!showCount || cancelled) {
						if (!cancelled) setAnnouncementCount(null);
						return;
					}

					const countResult = await client.query({
						query: queryAnnouncements,
						variables: {
							userId,
							overrideRole: !isModerator || undefined,
							first: 1,
						},
						fetchPolicy: 'network-only',
					});
					if (!cancelled) {
						const total = get(
							countResult,
							'data.queryAnnouncements.totalCount',
							0
						);
						setAnnouncementCount(total);
					}
				} catch (error) {
					console.error('Error fetching announcement count:', error);
				}
			})();

			return () => {
				cancelled = true;
			};
		}, [client, companyId, userId, isModerator]);

		const cardData = card.data || {};
		const baseTitle = cardData.title || 'Announcements';
		const title = announcementCount != null
			? `${baseTitle} (${announcementCount})`
			: baseTitle;
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#666666';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;
		const announcementsAmount = cardData.announcementsAmount || 3;

		const visibleAnnouncements = announcements
			.filter((announcement) => !announcement.hide)
			.slice(0, announcementsAmount);

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const htmlDecode = (input) => {
			let returnValue = input.replaceAll('&gt;', '>');
			returnValue = returnValue.replaceAll('&lt;', '<');
			returnValue = returnValue.replaceAll('&quot;', '"');
			returnValue = returnValue.replaceAll('&apos;', "'");
			returnValue = returnValue.replaceAll('&amp;', '&');
			return returnValue;
		};

		const formatDate = (dateString) => {
			const date = new Date(dateString);
			const options = {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			};
			return date.toLocaleDateString('en-US', options);
		};

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div
					className="d-card-head"
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '18px',
							margin: '0 0 10px',
							color: textColor,
						}}
					>
						{title}
					</h4>
					<Link
						to="/announcements"
						style={{
							fontSize: '12px',
							color: '#007bff',
							textDecoration: 'none',
							marginBottom: '10px',
							opacity: 0.8,
							transition: 'opacity 0.2s',
						}}
						onMouseEnter={(e) => (e.target.style.opacity = '1')}
						onMouseLeave={(e) => (e.target.style.opacity = '0.8')}
					>
						View All Announcements
					</Link>
				</div>
				<div
					className="flex-1"
					style={{
						overflowY: 'auto',
						overflowX: 'hidden',
						minHeight: 0,
					}}
				>
					<div className="space-y-4">
						{visibleAnnouncements.length > 0 ? (
							visibleAnnouncements.map((announcement) => (
								<div
									key={announcement.id}
									style={{
										paddingBottom: '12px',
										borderBottom: `1px solid ${borderColor}`,
									}}
								>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											marginBottom: '8px',
											gap: '8px',
										}}
									>
										<h5
											style={{
												fontSize: '13px',
												fontWeight: 600,
												margin: 0,
												color: textColor,
												fontFamily: 'Nunito Sans, sans-serif',
												flex: 1,
												minWidth: 0,
											}}
										>
											{announcement.title}
										</h5>
										<div
											style={{
												fontSize: '11px',
												color: textColor,
												opacity: 0.7,
												fontFamily: 'Nunito Sans, sans-serif',
												whiteSpace: 'nowrap',
											}}
										>
											{formatDate(announcement.dateCreated)}
										</div>
									</div>
									<div
										dangerouslySetInnerHTML={{
											__html: sanitizer(htmlDecode(announcement.content)),
										}}
										style={{
											fontSize: '12px',
											lineHeight: '1.5',
											color: textColor,
											fontFamily: 'Nunito Sans, sans-serif',
										}}
									/>
								</div>
							))
						) : (
							<div
								style={{
									padding: '20px',
									textAlign: 'center',
									color: textColor,
									opacity: 0.6,
									fontSize: '13px',
									fontFamily: 'Nunito Sans, sans-serif',
								}}
							>
								No announcements available
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
);

AnnouncementsCard.displayName = 'AnnouncementsCard';

export default AnnouncementsCard;
